import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';

interface EmailCampaign {
    id: number;
    sujet: string;
    template_name?: string | null;
    content_source: string;
    audience: string;
    statut: string;
    created_at?: string | null;
    test_envoye_at?: string | null;
    sent_at?: string | null;
    destinataires_count?: number | null;
}

interface AudienceCounts {
    PRO: number;
    ALL: number;
}

const STATUT_STYLES: Record<string, { bg: string; fg: string; border: string; label: string }> = {
    BROUILLON: { bg: '#dbeafe', fg: '#1e40af', border: '#93c5fd', label: 'Brouillon' },
    ENVOYE: { bg: '#d1fae5', fg: '#065f46', border: '#6ee7b7', label: 'Envoyée' },
};

const AUDIENCE_LABELS: Record<string, string> = {
    PRO: 'Abonnés Pro',
    ALL: 'Tous les utilisateurs',
};

const StatutBadge: React.FC<{ statut: string }> = ({ statut }) => {
    const style = STATUT_STYLES[statut] || STATUT_STYLES.BROUILLON;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '11px',
            fontWeight: 600, letterSpacing: '0.04em', backgroundColor: style.bg, color: style.fg,
            border: `1px solid ${style.border}`,
        }}>
            {style.label}
        </span>
    );
};

const inputStyle: React.CSSProperties = {
    padding: '6px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px',
    backgroundColor: 'var(--bg-input, white)',
};

const AdminEmailCampaignsScreen: React.FC = () => {
    const { token, hasPermission } = useAuth();
    const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
    const [templates, setTemplates] = useState<string[]>([]);
    const [counts, setCounts] = useState<AudienceCounts | null>(null);
    const [loading, setLoading] = useState(false);

    const [newSujet, setNewSujet] = useState('');
    const [newTemplate, setNewTemplate] = useState('');
    const [newAudience, setNewAudience] = useState('PRO');
    const [creating, setCreating] = useState(false);
    const [createMessage, setCreateMessage] = useState('');

    const [expandedId, setExpandedId] = useState<number | null>(null);
    const [editSujet, setEditSujet] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editAudience, setEditAudience] = useState('PRO');
    const [previewHtml, setPreviewHtml] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);
    const [actionMessage, setActionMessage] = useState<Record<number, string>>({});

    const authHeaders = useCallback((): Record<string, string> => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchCampaigns = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/v1/admin/email-campaigns`, { headers: authHeaders() });
            if (response.ok) {
                const data = await response.json();
                setCampaigns(Array.isArray(data) ? data : []);
            } else {
                setCampaigns([]);
            }
        } catch (error) {
            console.error('Error fetching email campaigns:', error);
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    }, [token, authHeaders]);

    const fetchTemplatesAndCounts = useCallback(async () => {
        if (!token) return;
        try {
            const [tplResponse, countsResponse] = await Promise.all([
                fetch(`${API_URL}/v1/admin/email-campaigns/templates`, { headers: authHeaders() }),
                fetch(`${API_URL}/v1/admin/email-campaigns/audience-counts`, { headers: authHeaders() }),
            ]);
            if (tplResponse.ok) {
                const data = await tplResponse.json();
                setTemplates(Array.isArray(data.data) ? data.data : []);
            }
            if (countsResponse.ok) {
                const data = await countsResponse.json();
                setCounts(data.data ?? null);
            }
        } catch (error) {
            console.error('Error fetching templates/counts:', error);
        }
    }, [token, authHeaders]);

    useEffect(() => {
        if (token && hasPermission('admin_platform')) {
            fetchCampaigns();
            fetchTemplatesAndCounts();
        }
    }, [token, hasPermission, fetchCampaigns, fetchTemplatesAndCounts]);

    const handleCreate = async () => {
        if (!newSujet.trim() || !newTemplate) {
            setCreateMessage('Sujet et template requis.');
            return;
        }
        setCreating(true);
        setCreateMessage('');
        try {
            const response = await fetch(`${API_URL}/v1/admin/email-campaigns`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ sujet: newSujet, template_name: newTemplate, audience: newAudience }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setCreateMessage('✅ Campagne créée.');
                setNewSujet('');
                setNewTemplate('');
                fetchCampaigns();
            } else {
                setCreateMessage((data as { detail?: string }).detail || 'Erreur lors de la création.');
            }
        } catch (error) {
            setCreateMessage('Erreur réseau.');
        } finally {
            setCreating(false);
        }
    };

    const fetchPreview = useCallback(async (campaignId: number) => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/email-campaigns/${campaignId}/preview`, { headers: authHeaders() });
            if (response.ok) {
                setPreviewHtml(await response.text());
            } else {
                const data = await response.json().catch(() => ({}));
                setPreviewHtml(`<p style="color:#ef4444;">${(data as { detail?: string }).detail || 'Erreur de prévisualisation.'}</p>`);
            }
        } catch (error) {
            setPreviewHtml('<p style="color:#ef4444;">Erreur réseau lors de la prévisualisation.</p>');
        }
    }, [authHeaders]);

    const handleExpand = (campaign: EmailCampaign) => {
        if (expandedId === campaign.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(campaign.id);
        setEditSujet(campaign.sujet);
        setEditContent(campaign.content_source);
        setEditAudience(campaign.audience);
        setPreviewHtml('');
        setActionMessage(prev => ({ ...prev, [campaign.id]: '' }));
        fetchPreview(campaign.id);
    };

    const handleSave = async (campaignId: number) => {
        setSavingId(campaignId);
        try {
            const response = await fetch(`${API_URL}/v1/admin/email-campaigns/${campaignId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ sujet: editSujet, content_source: editContent, audience: editAudience }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setActionMessage(prev => ({ ...prev, [campaignId]: '✅ Modifications enregistrées.' }));
                fetchCampaigns();
                fetchPreview(campaignId);
            } else {
                setActionMessage(prev => ({ ...prev, [campaignId]: (data as { detail?: string }).detail || 'Erreur lors de la sauvegarde.' }));
            }
        } catch (error) {
            setActionMessage(prev => ({ ...prev, [campaignId]: 'Erreur réseau.' }));
        } finally {
            setSavingId(null);
        }
    };

    const handleSendTest = async (campaignId: number) => {
        try {
            const response = await fetch(`${API_URL}/v1/admin/email-campaigns/${campaignId}/send-test`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setActionMessage(prev => ({ ...prev, [campaignId]: `✅ Test envoyé à ${(data as { sent_to?: string }).sent_to}.` }));
                fetchCampaigns();
            } else {
                setActionMessage(prev => ({ ...prev, [campaignId]: (data as { detail?: string }).detail || "Erreur lors de l'envoi du test." }));
            }
        } catch (error) {
            setActionMessage(prev => ({ ...prev, [campaignId]: 'Erreur réseau.' }));
        }
    };

    const handleSend = async (campaign: EmailCampaign) => {
        const count = counts ? counts[campaign.audience as keyof AudienceCounts] : null;
        const audienceLabel = AUDIENCE_LABELS[campaign.audience] || campaign.audience;
        const confirmText = count !== null
            ? `Envoyer « ${campaign.sujet} » à ${audienceLabel} (${count} destinataire(s)) ? Cette action est irréversible.`
            : `Envoyer « ${campaign.sujet} » à ${audienceLabel} ? Cette action est irréversible.`;
        if (!window.confirm(confirmText)) return;
        try {
            const response = await fetch(`${API_URL}/v1/admin/email-campaigns/${campaign.id}/send`, {
                method: 'POST',
                headers: authHeaders(),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                const d = data as { sent_count?: number; total_candidates?: number };
                setActionMessage(prev => ({ ...prev, [campaign.id]: `✅ Envoyée à ${d.sent_count ?? 0}/${d.total_candidates ?? 0} destinataire(s).` }));
                fetchCampaigns();
            } else {
                setActionMessage(prev => ({ ...prev, [campaign.id]: (data as { detail?: string }).detail || "Erreur lors de l'envoi." }));
            }
        } catch (error) {
            setActionMessage(prev => ({ ...prev, [campaign.id]: 'Erreur réseau.' }));
        }
    };

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box><p style={{ padding: '20px' }}>Accès refusé.</p></Box>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <Header icon='campaign' title='Campagnes Email' />

            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>Campagnes ponctuelles (IPO, annonces marché) envoyées aux abonnés Pro ou à tous les utilisateurs</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                        Créez une campagne depuis un template email, relisez/corrigez le contenu, envoyez-vous un
                        test, puis déclenchez l'envoi vers l'audience choisie. Une campagne envoyée ne peut jamais
                        être renvoyée (créer une nouvelle campagne si besoin).
                        {counts && (
                            <span> Audiences actuelles : <b>{counts.PRO}</b> abonné(s) Pro · <b>{counts.ALL}</b> utilisateur(s) actif(s).</span>
                        )}
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', padding: '14px', border: '1px dashed #4f46e5', borderRadius: '8px' }}>
                        <input
                            type="text" placeholder="Sujet de l'email (ex: IPO Bridge Bank : notre analyse)" value={newSujet}
                            onChange={e => setNewSujet(e.target.value)} style={{ ...inputStyle, flex: '1 1 280px' }}
                        />
                        <select value={newTemplate} onChange={e => setNewTemplate(e.target.value)} style={inputStyle}>
                            <option value="">— Choisir un template —</option>
                            {templates.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <select value={newAudience} onChange={e => setNewAudience(e.target.value)} style={inputStyle}>
                            <option value="PRO">Abonnés Pro</option>
                            <option value="ALL">Tous les utilisateurs</option>
                        </select>
                        <button
                            onClick={handleCreate} disabled={creating}
                            style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                        >
                            {creating ? 'Création...' : '➕ Nouvelle campagne'}
                        </button>
                        {createMessage && <span style={{ fontSize: '13px', color: '#475569' }}>{createMessage}</span>}
                    </div>

                    {loading ? (
                        <p>Chargement...</p>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table className='data-table' style={{ width: '100%' }}>
                                <thead>
                                    <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Créée le</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Sujet</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'left' }}>Audience</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'center' }}>Statut</th>
                                        <th style={{ color: 'white', padding: '14px 16px', textAlign: 'right' }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaigns.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                                Aucune campagne pour le moment.
                                            </td>
                                        </tr>
                                    ) : campaigns.map((c, idx) => (
                                        <React.Fragment key={c.id}>
                                            <tr style={{ backgroundColor: idx % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.created_at ? c.created_at.slice(0, 10) : '—'}</td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                                                    <span onClick={() => handleExpand(c)} style={{ cursor: 'pointer', textDecoration: 'underline dotted' }}>
                                                        {c.sujet}
                                                    </span>
                                                    {c.test_envoye_at && c.statut !== 'ENVOYE' && (
                                                        <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, color: '#b45309' }}>
                                                            🧪 Test envoyé
                                                        </span>
                                                    )}
                                                    {c.statut === 'ENVOYE' && (
                                                        <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 700, color: '#0369a1' }}>
                                                            ✉️ Envoyée ({c.destinataires_count ?? 0})
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{AUDIENCE_LABELS[c.audience] || c.audience}</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                                                    <StatutBadge statut={c.statut} />
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                    <button
                                                        onClick={() => handleExpand(c)}
                                                        style={{ backgroundColor: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px' }}
                                                    >
                                                        {expandedId === c.id ? 'Fermer' : 'Voir / Éditer'}
                                                    </button>
                                                    {c.statut !== 'ENVOYE' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleSendTest(c.id)}
                                                                style={{ backgroundColor: '#f59e0b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginRight: '8px' }}
                                                            >
                                                                🧪 M'envoyer un test
                                                            </button>
                                                            <button
                                                                onClick={() => handleSend(c)}
                                                                style={{ backgroundColor: '#0ea5e9', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
                                                            >
                                                                Envoyer
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                            {expandedId === c.id && (
                                                <tr>
                                                    <td colSpan={5} style={{ padding: '16px', backgroundColor: 'var(--bg-hover)' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Sujet</label>
                                                                <input
                                                                    type="text" value={editSujet} onChange={e => setEditSujet(e.target.value)}
                                                                    disabled={c.statut === 'ENVOYE'}
                                                                    style={{ ...inputStyle, width: '100%', marginBottom: '10px', boxSizing: 'border-box' }}
                                                                />
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Audience</label>
                                                                <select
                                                                    value={editAudience} onChange={e => setEditAudience(e.target.value)}
                                                                    disabled={c.statut === 'ENVOYE'}
                                                                    style={{ ...inputStyle, marginBottom: '10px' }}
                                                                >
                                                                    <option value="PRO">Abonnés Pro</option>
                                                                    <option value="ALL">Tous les utilisateurs</option>
                                                                </select>
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Source (Jinja/HTML)</label>
                                                                <textarea
                                                                    value={editContent} onChange={e => setEditContent(e.target.value)}
                                                                    rows={14} disabled={c.statut === 'ENVOYE'}
                                                                    style={{ ...inputStyle, width: '100%', fontFamily: 'monospace', fontSize: '12px', boxSizing: 'border-box' }}
                                                                />
                                                                {c.statut !== 'ENVOYE' && (
                                                                    <button
                                                                        onClick={() => handleSave(c.id)}
                                                                        disabled={savingId === c.id}
                                                                        className='button button-purple'
                                                                        style={{ marginTop: '10px', padding: '8px 16px' }}
                                                                    >
                                                                        {savingId === c.id ? 'Enregistrement...' : 'Enregistrer les modifications'}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Aperçu (rendu réel avec votre prénom)</label>
                                                                <iframe
                                                                    title={`Aperçu campagne ${c.id}`}
                                                                    srcDoc={previewHtml}
                                                                    style={{ width: '100%', height: '460px', border: '1px solid #ddd', borderRadius: '6px', backgroundColor: 'white' }}
                                                                />
                                                            </div>
                                                        </div>
                                                        {actionMessage[c.id] && (
                                                            <p style={{ marginTop: '12px', fontSize: '13px', color: '#475569' }}>{actionMessage[c.id]}</p>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Box>
        </SiteLayout>
    );
};

export default AdminEmailCampaignsScreen;
